import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  LinearProgress
} from '@mui/material';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinkIcon from '@mui/icons-material/Link';

const GOLD = '#d4af37';

interface BoatingItem {
  id: string;
  name: string;
  type: string;
  price: string;
  duration: string;
  location: string;
  description: string;
  imageUrl: string;
  website: string;
}

interface BoatingContent {
  title: string;
  description: string;
  boating: BoatingItem[];
  akalappuzhaImages: string[];
}

const BoatingNearbyManager = () => {
  const [content, setContent] = useState<BoatingContent>({
    title: 'Boating Activities',
    description: 'Enjoy boating and water activities in the area',
    boating: [
      { id: '1', name: 'Backwater Cruise', type: 'Cruise', price: '₹500-1000', duration: '2-3 hours', location: 'Backwaters', description: 'Scenic backwater cruise through the region', imageUrl: '', website: '' },
      { id: '2', name: 'Kayaking', type: 'Adventure', price: '₹300-500', duration: '1-2 hours', location: 'Lake/Backwaters', description: 'Exciting kayaking experience in calm waters', imageUrl: '', website: '' },
    ],
    akalappuzhaImages: [
      'https://img.mathrubhumi.com/view/acePublic/alias/contentid/1lwgsfedrrwuduheunn/0/akalappuzha.webp?f=3%3A2&q=0.75&w=900',
      'https://www.keralatourism.org/images/microsites/payyoli/img/akalappuzha-backwaters-2.jpg',
      'https://www.keralatourism.org/images/microsites/payyoli/img/akalappuzha-boating.jpg',
    ]
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [uploadingIdx, setUploadingIdx] = useState<number | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingBoatingId, setUploadingBoatingId] = useState<string | null>(null);
  const [boatingUploadProgress, setBoatingUploadProgress] = useState(0);
  const galleryFileInputRef = useRef<HTMLInputElement>(null);
  const boatingFileInputRef = useRef<HTMLInputElement>(null);
  const pendingGalleryIdx = useRef<number | null>(null);
  const pendingBoatingId = useRef<string | null>(null);
  const galleryFocusHandler = useRef<(() => void) | null>(null);
  const boatingFocusHandler = useRef<(() => void) | null>(null);

  const loadContent = useCallback(() => {
    try {
      return onSnapshot(doc(db, 'nearby_content', 'boating'), (docSnap) => {
        if (docSnap.exists()) {
          setContent(docSnap.data() as BoatingContent);
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error loading content:', error);
      setLoading(false);
      showSnackbar('Failed to load content', 'error');
      return () => {};
    }
  }, []);

  useEffect(() => {
    const unsubscribe = loadContent();
    return () => unsubscribe();
  }, [loadContent]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'nearby_content', 'boating'), content);
      showSnackbar('Boating data saved successfully', 'success');
    } catch (error) {
      console.error('Error saving:', error);
      showSnackbar('Failed to save content', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAddBoating = () => {
    const newBoating: BoatingItem = {
      id: Date.now().toString(),
      name: 'New Boating Activity',
      type: 'Cruise',
      price: '',
      duration: '',
      location: '',
      description: '',
      imageUrl: '',
      website: '',
    };
    setContent({
      ...content,
      boating: [...content.boating, newBoating]
    });
  };

  const handleBoatingChange = (id: string, field: keyof BoatingItem, value: string) => {
    setContent({
      ...content,
      boating: content.boating.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    });
  };

  const handleDeleteBoating = (id: string) => {
    setContent({
      ...content,
      boating: content.boating.filter(item => item.id !== id)
    });
  };

  // ─── Auto-compress image to base64 ───
  const compressImage = (file: File, maxWidth = 1920, maxHeight = 1080, quality = 0.92): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        let { width, height } = img;
        // Scale down if needed
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d')!;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        let base64 = canvas.toDataURL('image/jpeg', quality);
        // If too large for Firestore (~1MB doc limit), reduce quality gradually
        let q = quality;
        while (base64.length > 900_000 && q > 0.5) {
          q -= 0.05;
          base64 = canvas.toDataURL('image/jpeg', q);
        }
        // If still too large, scale down
        if (base64.length > 900_000) {
          const smallCanvas = document.createElement('canvas');
          const scale = 0.75;
          smallCanvas.width = Math.round(width * scale);
          smallCanvas.height = Math.round(height * scale);
          const sCtx = smallCanvas.getContext('2d')!;
          sCtx.imageSmoothingEnabled = true;
          sCtx.imageSmoothingQuality = 'high';
          sCtx.drawImage(canvas, 0, 0, smallCanvas.width, smallCanvas.height);
          base64 = smallCanvas.toDataURL('image/jpeg', 0.8);
        }
        resolve(base64);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };
      img.src = url;
    });
  };

  // ─── Gallery Image Upload (auto-compressed) ───
  const handleGalleryFileUpload = async (idx: number, file: File) => {
    if (!file.type.startsWith('image/')) {
      showSnackbar('Please select an image file', 'error');
      return;
    }
    setUploadingIdx(idx);
    setUploadProgress(10);
    try {
      setUploadProgress(30);
      const base64 = await compressImage(file);
      setUploadProgress(90);
      const updated = [...(content.akalappuzhaImages || [])];
      updated[idx] = base64;
      setContent({ ...content, akalappuzhaImages: updated });
      setUploadProgress(100);
      const originalKB = Math.round(file.size / 1024);
      const compressedKB = Math.round((base64.length * 3) / 4 / 1024);
      setTimeout(() => {
        setUploadingIdx(null);
        setUploadProgress(0);
        showSnackbar(`Image compressed (${originalKB}KB → ${compressedKB}KB) & uploaded! Click "Save Changes" to persist.`, 'success');
      }, 400);
    } catch {
      showSnackbar('Failed to process image', 'error');
      setUploadingIdx(null);
      setUploadProgress(0);
    }
  };

  // ─── Boating Activity Image Upload (auto-compressed) ───
  const handleBoatingFileUpload = async (id: string, file: File) => {
    if (!file.type.startsWith('image/')) {
      showSnackbar('Please select an image file', 'error');
      return;
    }
    setUploadingBoatingId(id);
    setBoatingUploadProgress(10);
    try {
      setBoatingUploadProgress(30);
      const base64 = await compressImage(file);
      setBoatingUploadProgress(90);
      setContent({
        ...content,
        boating: content.boating.map(item =>
          item.id === id ? { ...item, imageUrl: base64 } : item
        )
      });
      setBoatingUploadProgress(100);
      const originalKB = Math.round(file.size / 1024);
      const compressedKB = Math.round((base64.length * 3) / 4 / 1024);
      setTimeout(() => {
        setUploadingBoatingId(null);
        setBoatingUploadProgress(0);
        showSnackbar(`Image compressed (${originalKB}KB → ${compressedKB}KB) & uploaded! Click "Save Changes" to persist.`, 'success');
      }, 400);
    } catch {
      showSnackbar('Failed to process image', 'error');
      setUploadingBoatingId(null);
      setBoatingUploadProgress(0);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
            Boating Activities Manager
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage boating and water activities content
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadContent}
            disabled={loading}
            variant="outlined"
            sx={{ borderColor: GOLD, color: GOLD, '&:hover': { bgcolor: `${GOLD}15` } }}
          >
            Refresh
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          ⛵ Section Settings
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Section Title"
              value={content.title}
              onChange={(e) => setContent({ ...content, title: e.target.value })}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Section Description"
              value={content.description}
              onChange={(e) => setContent({ ...content, description: e.target.value })}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Akalappuzha Gallery Images */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD }}>
            🌴 Akalappuzha Gallery Images ({(content.akalappuzhaImages || []).length})
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={() => setContent({ ...content, akalappuzhaImages: [...(content.akalappuzhaImages || []), ''] })}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            Add Image
          </Button>
        </Box>
        <Typography variant="body2" sx={{ color: '#888', mb: 2 }}>
          These images appear in the Akalappuzha Tourism image carousel on the boating page. You can paste a URL or upload an image from your device (max 1MB).
        </Typography>
        {/* Hidden file input for gallery */}
        <input
          type="file"
          accept="image/*"
          ref={galleryFileInputRef}
          style={{ display: 'none' }}
          onChange={(e) => {
            // Remove cancel-detection listener since onChange fired
            if (galleryFocusHandler.current) {
              window.removeEventListener('focus', galleryFocusHandler.current);
              galleryFocusHandler.current = null;
            }
            const file = e.target.files?.[0];
            if (file && pendingGalleryIdx.current !== null) {
              handleGalleryFileUpload(pendingGalleryIdx.current, file);
            }
            pendingGalleryIdx.current = null;
            e.target.value = '';
          }}
        />
        <Grid container spacing={2}>
          {(content.akalappuzhaImages || []).map((imgUrl, idx) => (
            <Grid item xs={12} key={idx}>
              <Paper sx={{ p: 2, bgcolor: '#fff', border: `1px solid ${GOLD}` }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={1}>
                    <Typography sx={{ fontWeight: 700, color: GOLD, textAlign: 'center' }}>#{idx + 1}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={imgUrl ? 5 : 8}>
                    <TextField
                      fullWidth
                      label={`Image URL ${idx + 1}`}
                      value={imgUrl && imgUrl.startsWith('data:') ? '(Uploaded image)' : imgUrl}
                      onChange={(e) => {
                        const updated = [...(content.akalappuzhaImages || [])];
                        updated[idx] = e.target.value;
                        setContent({ ...content, akalappuzhaImages: updated });
                      }}
                      size="small"
                      placeholder="https://example.com/image.jpg"
                      disabled={imgUrl?.startsWith('data:')}
                      InputProps={{
                        startAdornment: <LinkIcon sx={{ fontSize: 18, color: '#999', mr: 1 }} />,
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      disabled={uploadingIdx === idx}
                      onClick={() => {
                        pendingGalleryIdx.current = idx;
                        galleryFileInputRef.current?.click();
                        // Reset if dialog cancelled (window regains focus without file)
                        const onFocus = () => {
                          setTimeout(() => {
                            if (pendingGalleryIdx.current === idx) {
                              pendingGalleryIdx.current = null;
                            }
                            setUploadingIdx((prev) => prev === idx ? null : prev);
                            setUploadProgress(0);
                          }, 300);
                          window.removeEventListener('focus', onFocus);
                          galleryFocusHandler.current = null;
                        };
                        galleryFocusHandler.current = onFocus;
                        window.addEventListener('focus', onFocus);
                      }}
                      sx={{
                        borderColor: GOLD, color: GOLD, fontWeight: 600, fontSize: '0.75rem',
                        textTransform: 'none', height: 40,
                        '&:hover': { bgcolor: `${GOLD}15`, borderColor: GOLD },
                      }}
                    >
                      {uploadingIdx === idx ? 'Uploading...' : 'Upload'}
                    </Button>
                    {uploadingIdx === idx && (
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress}
                        sx={{ mt: 0.5, borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: GOLD } }}
                      />
                    )}
                  </Grid>
                  {imgUrl && (
                    <Grid item xs={12} sm={3}>
                      <Box
                        component="img"
                        src={imgUrl}
                        alt={`Preview ${idx + 1}`}
                        sx={{ width: '100%', height: 60, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd' }}
                        onError={(e: any) => { e.target.style.display = 'none'; }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={1}>
                    <IconButton
                      onClick={() => {
                        const updated = (content.akalappuzhaImages || []).filter((_, i) => i !== idx);
                        setContent({ ...content, akalappuzhaImages: updated });
                      }}
                      color="error"
                      sx={{ width: '100%' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                  {imgUrl && imgUrl.startsWith('data:') && (
                    <Grid item xs={12}>
                      <Button
                        size="small"
                        startIcon={<LinkIcon />}
                        onClick={() => {
                          const updated = [...(content.akalappuzhaImages || [])];
                          updated[idx] = '';
                          setContent({ ...content, akalappuzhaImages: updated });
                        }}
                        sx={{ textTransform: 'none', fontSize: '0.72rem', color: '#888' }}
                      >
                        Switch to URL input
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD }}>
            🚤 Boating Activities ({content.boating.length})
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddBoating}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            Add Activity
          </Button>
        </Box>

        <Grid container spacing={2}>
          {content.boating.map((item) => (
            <Grid item xs={12} key={item.id}>
              <Paper sx={{ p: 2, bgcolor: '#fff', border: `1px solid ${GOLD}` }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      label="Activity Name"
                      value={item.name}
                      onChange={(e) => handleBoatingChange(item.id, 'name', e.target.value)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Type"
                      value={item.type}
                      onChange={(e) => handleBoatingChange(item.id, 'type', e.target.value)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Price"
                      value={item.price}
                      onChange={(e) => handleBoatingChange(item.id, 'price', e.target.value)}
                      size="small"
                      placeholder="e.g., ₹500-1000"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <IconButton
                      onClick={() => handleDeleteBoating(item.id)}
                      color="error"
                      sx={{ width: '100%' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Duration"
                      value={item.duration}
                      onChange={(e) => handleBoatingChange(item.id, 'duration', e.target.value)}
                      size="small"
                      placeholder="e.g., 2-3 hours"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Location"
                      value={item.location}
                      onChange={(e) => handleBoatingChange(item.id, 'location', e.target.value)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={item.description}
                      onChange={(e) => handleBoatingChange(item.id, 'description', e.target.value)}
                      multiline
                      rows={2}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Image URL"
                      value={item.imageUrl && item.imageUrl.startsWith('data:') ? '(Uploaded image)' : (item.imageUrl || '')}
                      onChange={(e) => handleBoatingChange(item.id, 'imageUrl', e.target.value)}
                      size="small"
                      placeholder="https://example.com/image.jpg"
                      disabled={item.imageUrl?.startsWith('data:')}
                      InputProps={{
                        startAdornment: <LinkIcon sx={{ fontSize: 18, color: '#999', mr: 1 }} />,
                      }}
                      helperText="Paste a URL or upload an image (max 1MB)"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        disabled={uploadingBoatingId === item.id}
                        onClick={() => {
                          pendingBoatingId.current = item.id;
                          boatingFileInputRef.current?.click();
                          const onFocus = () => {
                            setTimeout(() => {
                              if (pendingBoatingId.current === item.id) {
                                pendingBoatingId.current = null;
                              }
                              setUploadingBoatingId((prev) => prev === item.id ? null : prev);
                              setBoatingUploadProgress(0);
                            }, 300);
                            window.removeEventListener('focus', onFocus);
                            boatingFocusHandler.current = null;
                          };
                          boatingFocusHandler.current = onFocus;
                          window.addEventListener('focus', onFocus);
                        }}
                        sx={{
                          borderColor: GOLD, color: GOLD, fontWeight: 600, fontSize: '0.72rem',
                          textTransform: 'none', height: 34,
                          '&:hover': { bgcolor: `${GOLD}15`, borderColor: GOLD },
                        }}
                      >
                        {uploadingBoatingId === item.id ? 'Uploading...' : 'Upload Image'}
                      </Button>
                      {item.imageUrl?.startsWith('data:') && (
                        <Button
                          size="small"
                          startIcon={<LinkIcon />}
                          onClick={() => handleBoatingChange(item.id, 'imageUrl', '')}
                          sx={{ textTransform: 'none', fontSize: '0.7rem', color: '#888' }}
                        >
                          Clear & use URL
                        </Button>
                      )}
                    </Box>
                    {uploadingBoatingId === item.id && (
                      <LinearProgress
                        variant="determinate"
                        value={boatingUploadProgress}
                        sx={{ mt: 0.5, borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: GOLD } }}
                      />
                    )}
                    {item.imageUrl && (
                      <Box
                        component="img"
                        src={item.imageUrl}
                        alt="Preview"
                        sx={{ width: '100%', height: 60, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd', mt: 1 }}
                        onError={(e: any) => { e.target.style.display = 'none'; }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Website URL"
                      value={item.website || ''}
                      onChange={(e) => handleBoatingChange(item.id, 'website', e.target.value)}
                      size="small"
                      placeholder="https://example.com"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Hidden file input for boating activity images */}
      <input
        type="file"
        accept="image/*"
        ref={boatingFileInputRef}
        style={{ display: 'none' }}
        onChange={(e) => {
          // Remove cancel-detection listener since onChange fired
          if (boatingFocusHandler.current) {
            window.removeEventListener('focus', boatingFocusHandler.current);
            boatingFocusHandler.current = null;
          }
          const file = e.target.files?.[0];
          if (file && pendingBoatingId.current) {
            handleBoatingFileUpload(pendingBoatingId.current, file);
          }
          pendingBoatingId.current = null;
          e.target.value = '';
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BoatingNearbyManager;
